import { Injectable, inject, signal } from '@angular/core';
import { AuthConfig, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { JwtPayload } from 'jwt-decode';
import { Observable, Subject } from 'rxjs';

import { User } from '../models/user';
import { AUTH_ENVIRONMENT } from './auth.config';
import { JwtService } from './jwt.service';
import { UserProfileUpdate } from './user-access.model';

export type SilentRefreshStatus = 'unknown' | 'checking' | 'available' | 'unavailable';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oAuthService = inject(OAuthService);
  private readonly authEnvironment = inject(AUTH_ENVIRONMENT);
  private readonly jwtService = inject(JwtService);

  private readonly loggedOutSubject = new Subject<void>();
  private readonly userInformationState = signal<User | null>(null);
  private readonly accessTokenExpiryState = signal<number | null>(null);
  private readonly silentRefreshStatusState = signal<SilentRefreshStatus>('unknown');

  private configured = false;

  readonly userInformation = this.userInformationState.asReadonly();
  readonly accessTokenExpiry = this.accessTokenExpiryState.asReadonly();
  readonly silentRefreshStatus = this.silentRefreshStatusState.asReadonly();

  loggedOut(): Observable<void> {
    return this.loggedOutSubject.asObservable();
  }

  async authorize(requestedPathAfterLogin?: string): Promise<boolean> {
    this.configureOAuth();

    await this.oAuthService.loadDiscoveryDocumentAndTryLogin();

    if (await this.checkAccessTokenValidity()) {
      this.updateAccessTokenExpiry();
      await this.getUserInformation();
      return true;
    }

    const state = requestedPathAfterLogin ?? this.getLocationPathAndSearch();
    this.oAuthService.initCodeFlow(state);
    return false;
  }

  reauthenticateWithIdp(): void {
    this.configureOAuth();
    this.oAuthService.initCodeFlow(this.getLocationPathAndSearch());
  }

  async isLoggedIn(): Promise<boolean> {
    this.configureOAuth();
    return this.checkAccessTokenValidity();
  }

  async logout(): Promise<void> {
    this.userInformationState.set(null);
    this.accessTokenExpiryState.set(null);
    this.silentRefreshStatusState.set('unknown');
    this.loggedOutSubject.next();
    this.oAuthService.logOut();
  }

  async checkSilentRefreshAvailability(): Promise<void> {
    this.configureOAuth();
    this.silentRefreshStatusState.set('checking');

    try {
      await this.oAuthService.silentRefresh();
      this.updateAccessTokenExpiry();
      this.silentRefreshStatusState.set('available');
    } catch {
      this.silentRefreshStatusState.set('unavailable');
    }
  }

  getAccessToken(): string {
    return this.oAuthService.getAccessToken();
  }

  profileForSync(): UserProfileUpdate | null {
    const user = this.userInformationState();
    const name = user?.displayName.trim();
    if (!name) {
      return null;
    }
    return { name, email: user?.email.trim() || null };
  }

  async getUserInformation(): Promise<User> {
    const fallbackUser: User = {
      userId: '',
      displayName: '',
      firstName: '',
      lastName: '',
      email: '',
    };

    try {
      await this.oAuthService.loadUserProfile();
      const claims = this.oAuthService.getIdentityClaims() as Record<string, unknown>;
      const firstName = this.stringClaim(claims, 'given_name');
      const lastName = this.stringClaim(claims, 'family_name');
      const user: User = {
        userId: this.stringClaim(claims, 'sub'),
        displayName:
          this.stringClaim(claims, 'name') || [firstName, lastName].filter(Boolean).join(' '),
        firstName,
        lastName,
        email: this.stringClaim(claims, 'email'),
        locale: this.stringClaim(claims, 'locale') || undefined,
      };

      this.userInformationState.set(user);
      return user;
    } catch {
      const subject = this.getSubjectFromAccessToken();
      const user = {
        ...fallbackUser,
        userId: subject ? `${subject} (No personal user)` : 'No user found.',
      };
      this.userInformationState.set(user);
      return user;
    }
  }

  getAccessTokenLifetime(): number | null {
    const accessToken = this.oAuthService.getAccessToken();
    const decodedToken = this.jwtService.decodeToken(accessToken);
    if (!decodedToken.exp || !decodedToken.iat) {
      return null;
    }

    const lifetime = decodedToken.exp - decodedToken.iat;
    return lifetime > 0 ? lifetime * 1000 : null;
  }

  getRemainingAccessTokenLifetime(): number | null {
    const expiry = this.accessTokenExpiryState();
    if (!expiry) {
      return null;
    }

    return Math.max(0, expiry - Date.now());
  }

  isSilentRefreshUnavailable(): boolean {
    return this.silentRefreshStatusState() === 'unavailable';
  }

  private configureOAuth(): void {
    if (this.configured) {
      return;
    }

    const redirectUri = window.location.origin;
    const authCodeFlowConfig: AuthConfig = {
      issuer: this.authEnvironment.issuer,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
      logoutUrl: `${this.authEnvironment.issuer}/protocol/openid-connect/logout`,
      clientId: this.authEnvironment.clientId,
      silentRefreshRedirectUri: `${redirectUri}/silent-refresh.html`,
      silentRefreshTimeout: 5_000,
      useSilentRefresh: true,
      responseType: 'code',
      scope: this.authEnvironment.scope,
      showDebugInformation: this.authEnvironment.name !== 'prod',
      customQueryParams: {
        ppn_resource: this.authEnvironment.audience,
      },
    };

    this.oAuthService.events.subscribe(({ type }: OAuthEvent) => {
      const requestedPath = this.oAuthService.state;
      if (type === 'token_received' && requestedPath) {
        window.history.replaceState(null, '', decodeURIComponent(requestedPath));
      }

      if (type === 'silently_refreshed') {
        this.updateAccessTokenExpiry();
        this.silentRefreshStatusState.set('available');
      }

      if (type === 'silent_refresh_error') {
        this.silentRefreshStatusState.set('unavailable');
      }
    });

    this.oAuthService.configure(authCodeFlowConfig);
    this.oAuthService.setupAutomaticSilentRefresh();
    this.configured = true;
  }

  private async checkAccessTokenValidity(): Promise<boolean> {
    const accessToken = this.oAuthService.getAccessToken();

    if (!accessToken || !this.oAuthService.hasValidAccessToken()) {
      return false;
    }

    if (!(await this.checkAccessTokenSignature(accessToken))) {
      return false;
    }

    const decodedToken = this.jwtService.decodeToken(accessToken);
    return (
      this.checkAccessTokenExpiration(decodedToken) &&
      this.checkAccessTokenIssuer(decodedToken) &&
      this.checkAccessTokenAudience(decodedToken)
    );
  }

  private checkAccessTokenExpiration(token: JwtPayload): boolean {
    if (!token.exp) {
      return false;
    }

    return token.exp * 1000 > Date.now();
  }

  private checkAccessTokenIssuer(token: JwtPayload): boolean {
    return token.iss === this.authEnvironment.issuer;
  }

  private checkAccessTokenAudience(token: JwtPayload): boolean {
    const audience = token.aud;
    return Array.isArray(audience)
      ? audience.includes(this.authEnvironment.audience)
      : audience === this.authEnvironment.audience;
  }

  private async checkAccessTokenSignature(token: string): Promise<boolean> {
    try {
      await this.jwtService.verifyToken(token, {
        audience: this.authEnvironment.audience,
        issuer: this.authEnvironment.issuer,
        jwksPath: this.authEnvironment.jwksPath,
      });
      return true;
    } catch {
      return false;
    }
  }

  private getSubjectFromAccessToken(): string {
    const decodedToken = this.jwtService.decodeToken(this.oAuthService.getAccessToken());
    return decodedToken.sub ?? '';
  }

  private updateAccessTokenExpiry(): void {
    const accessToken = this.oAuthService.getAccessToken();
    const decodedToken = this.jwtService.decodeToken(accessToken);
    this.accessTokenExpiryState.set(decodedToken.exp ? decodedToken.exp * 1000 : null);
  }

  private getLocationPathAndSearch(): string {
    return `${window.location.pathname}${window.location.search}`;
  }

  private stringClaim(claims: Record<string, unknown>, name: string): string {
    const value = claims[name];
    return typeof value === 'string' ? value.trim() : '';
  }
}
